import styled from '@emotion/styled'
import { useEffect, useState } from 'react'

import { Space, StyledValidationMessage, TextInput, Typography } from '../../atoms'
import Icon from '../../atoms/Icon'
import IconButton from '../iconButton'
import LinkIcon from '../linkIcon'

export type TradeApproveProps = {
  allowComments?: boolean
  approveBtnCaption: string
  approvingSection: string
  canApprove: boolean
  onApprove: (comments: string) => void
  onReject?: (comments: string) => void
  userName: string
}

const StyledTextInput = styled.div`
  width: 240px;
`

const StyledTextArea = styled.div`
  textarea {
    max-width: 600px;
    width: 100%;
    min-width: 100%;
    height: 140px;
    color: var(--Graphite);
    border: solid 1px var(--White80);
    padding: 12px 8px;
    margin: 0;
    &:focus {
      outline: none;
    }
  }
`

export const TradeApprove = ({
  allowComments,
  approveBtnCaption,
  approvingSection,
  canApprove,
  onApprove,
  onReject,
  userName
}: TradeApproveProps) => {
  const [comments, setComments] = useState<string>('')
  const [validationError, setValidationError] = useState<string>('')
  const [actionEnabled, setActionEnabled] = useState<boolean>(false)
  const [enteredUserName, setEnteredUserName] = useState<string>('')

  useEffect(() => {
    setActionEnabled(canApprove)
    setValidationError(undefined)
  }, [canApprove, enteredUserName])

  const approvalText = `To approve the ${approvingSection}, enter your username below and click on the button.`
  const onChange = args => {
    setEnteredUserName(args.target.value)
  }
  const onCommentsChange = args => {
    setComments(args.target.value)
  }
  const isAllowed = () => {
    const isValid = enteredUserName === userName
    if (!isValid) {
      setValidationError(`Entered user name does not match with your username: ${userName}`)
      return false
    }
    return true
  }
  const onApproveHandler = () => {
    if (isAllowed()) {
      onApprove(comments)
    }
  }
  const onRejectHandler = () => {
    if (isAllowed()) {
      onReject(comments)
    }
  }
  return (
    <>
      {allowComments && (
        <Space my={6}>
          <StyledTextArea>
            <textarea value={comments} onChange={onCommentsChange} />
          </StyledTextArea>
        </Space>
      )}
      <Space my={5}>
        <Typography variant="b3">{approvalText}</Typography>
      </Space>
      <Space mt={5}>
        <StyledTextInput>
          <TextInput data-private value={enteredUserName} onChange={onChange} />
        </StyledTextInput>
        <StyledValidationMessage data-testid="userName_error" role="alert">
          {validationError}
        </StyledValidationMessage>
      </Space>
      <Space isflex alignItems="center" flexDirection="row" my={6}>
        <IconButton
          disabled={!actionEnabled}
          icon={<Icon name="check" size="small" />}
          iconPosition="left"
          operationType="progress"
          text={approveBtnCaption}
          onClick={onApproveHandler}
        />
        {onReject && (
          <Space mx={5}>
            <LinkIcon
              disabled={!actionEnabled}
              icon={<Icon name="remove" size="small" />}
              iconPosition="left"
              text="Reject"
              onClick={onRejectHandler}
            />
          </Space>
        )}
      </Space>
    </>
  )
}
